'use client'

/**
 * brand_showcase header — the "full storefront" header for adventure /
 * service businesses, counterpart to the brand_showcase footer.
 *
 * Defining behaviors:
 * - On the home page the bar floats transparent over the hero, then
 *   transitions to a fixed, solid, appearance-colored bar once the visitor
 *   scrolls (the scroll-reveal treatment `sticky_on_scroll` reserved).
 *   Off-home pages (no hero) render the solid state as a regular sticky bar.
 * - `logo_badge` renders the logo on a white badge plate that hangs below
 *   the bar (rounded shield silhouette) instead of sitting inside the row.
 * - `nav_case: 'uppercase'` renders top-level desktop nav links (and the CTA
 *   label) in caps; dropdown-panel entries keep their authored case.
 *
 * The variant is designed for imagery/dark surfaces, so text tokens default
 * to white and the surface to a dark slate unless `appearance` overrides
 * them — dropdown panels inherit the same dark surface via the token vars.
 *
 * Imports render-time internals from SiteHeader (same file-pair pattern as
 * SiteFooter ↔ BrandShowcaseFooter — safe because neither module touches the
 * other's exports at module-init time).
 */

import { useEffect, useState } from 'react'
import Link from 'next/link'
import type { NavLink } from '@sifly/shared-types'
import { formatPhone } from '@sifly/shared-utils'
import { MarkPlate } from './MarkPlate'
import {
  DesktopNav,
  HeaderActions,
  Logo,
  appearanceStyle,
  effectiveMenuEntries,
  type SiteHeaderProps,
} from './SiteHeader'

// Solid-state bar paint. Matches the near-opaque scrolled treatment of the
// reference sites this variant targets; hex comes from appearance.background_hex.
const SOLID_ALPHA = 0.94
// Past this the home-page bar switches transparent → fixed solid. Roughly the
// announcement-bar height, so the fixed bar lands as the announcement scrolls
// out instead of covering it.
const SCROLL_THRESHOLD = 48

// Variant surface defaults when `appearance` is partial. Dark slate + white
// text keep the transparent-over-hero state readable out of the box.
const DEFAULT_BG = '#0f172a'

// Badge plate dimensions per `logo_size` — the chrome slot the non-badge
// header logo already honored. Literal class strings: Tailwind's scanner
// can't see interpolated names.
//
// PLATE dimensions; the mark sits inside them inset by BADGE_CLEAR_SPACE.
// The mark is positioned by `markFillStyle` from the asset's
// `content_bounds`, so its box is the plate minus that inset — meaning each
// size renders the same apparent mark size for every dealer, regardless of
// whether their file is a tight crop, has transparent padding, or carries a
// baked-in backing plate.
const BADGE_PLATE_BY_SIZE = {
  sm: 'w-[44px] h-[42px] md:w-[60px] md:h-[58px]',
  md: 'w-[55px] h-[53px] md:w-[75px] md:h-[72px]',
  lg: 'w-[66px] h-[63px] md:w-[90px] md:h-[86px]',
  xl: 'w-[79px] h-[76px] md:w-[108px] md:h-[104px]',
} as const

function hexToRgba(hex: string, alpha: number): string {
  const m = /^#([0-9a-fA-F]{6})$/.exec(hex)
  if (!m) return hex
  const n = parseInt(m[1], 16)
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`
}

export function BrandShowcaseHeader(props: SiteHeaderProps) {
  const { tenant, settings, cartSlot, accountSlot, searchSlot, mobileNavSlot, logoUrl, isHomePage } = props
  const data = props.override?.data ?? {}
  const menuEntries: NavLink[] = effectiveMenuEntries(
    settings?.menu_links?.entries ?? [],
    tenant.site_tier,
    props.defaultMenuEntries,
  )
  const solidHex = data.appearance?.background_hex ?? DEFAULT_BG
  const uppercase = data.nav_case === 'uppercase'
  const badge = data.logo_badge?.enabled === true && !!logoUrl
  const logoSize = data.logo_size ?? 'md'
  // `static` opts out of the fixed-on-scroll reveal — the bar stays a
  // transparent overlay on home and scrolls away with the page.
  const fixesOnScroll = data.sticky_behavior !== 'static'
  const overlayMode = isHomePage === true
  // Same public-phone gate as dense_utility and the brand_showcase footer —
  // the number is tenant data, not a chrome slot.
  const phone = tenant.show_public_phone !== false ? tenant.phone : null

  const [scrolled, setScrolled] = useState(false)
  useEffect(() => {
    if (!overlayMode || !fixesOnScroll) return
    const onScroll = () => setScrolled(window.scrollY > SCROLL_THRESHOLD)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [overlayMode, fixesOnScroll])

  const solid = !overlayMode || scrolled

  // Token overrides: appearance wins; otherwise paint the variant's dark
  // defaults so DesktopNav / dropdown panels / slots inherit sane colors.
  const style: Record<string, string> = {
    ...(appearanceStyle(data.appearance) as Record<string, string> | undefined),
  }
  if (!data.appearance?.background_hex) {
    style['--color-bg-primary'] = DEFAULT_BG
    style['--color-bg-secondary'] = DEFAULT_BG
  }
  if (!data.appearance?.text_hex) {
    style['--color-text-primary'] = '#ffffff'
    style['--color-text-secondary'] = 'rgba(255,255,255,0.9)'
    style['--color-text-muted'] = 'rgba(255,255,255,0.7)'
  }
  if (!data.appearance?.border_hex) {
    style['--color-border'] = 'rgba(255,255,255,0.2)'
  }

  const barPosition = overlayMode
    ? scrolled && fixesOnScroll
      ? 'fixed inset-x-0 top-0'
      : 'absolute inset-x-0 top-0'
    : ''

  return (
    <header
      // Home: zero-height flow slot so the hero below starts at the header's
      // position and the transparent bar paints over its top. Off-home:
      // regular sticky bar with real flow height.
      className={overlayMode ? 'relative z-40 h-0' : 'sticky top-0 z-40'}
      style={style as React.CSSProperties}
      data-component="SiteHeader"
      data-variant="brand_showcase"
      data-overlay={overlayMode && !solid ? '1' : '0'}
      data-section-ref="chrome:header:brand_showcase"
    >
      <div
        // Translucency + blur exist for the home overlay, where page content
        // scrolls behind the fixed bar. Off-home nothing is ever behind the
        // bar, so it paints fully opaque — the alpha bought nothing there, and
        // backdrop-filter layers are prone to dropping out of full-page
        // captures/compositing edge cases, which reads as a white bar with
        // white nav text.
        className={`${barPosition} z-40 transition-[background-color,box-shadow] duration-300 ${solid ? `shadow-md${overlayMode ? ' backdrop-blur-sm' : ''}` : ''}`}
        style={{ backgroundColor: solid ? (overlayMode ? hexToRgba(solidHex, SOLID_ALPHA) : solidHex) : 'transparent' }}
      >
        <div className="mx-auto max-w-[var(--max-width-page)] px-4 h-16 md:h-[70px] flex items-center justify-between gap-4">
          {badge ? (
            <Link
              href="/"
              className="shrink-0 self-start"
              data-chrome-target="header:logo"
              aria-label={tenant.name}
            >
              {/* Plate size is driven by the `logo_size` chrome slot via
                  BADGE_PLATE_BY_SIZE — resize via set_chrome, not by editing
                  classes here. MarkPlate handles the mark sizing + clear
                  space, shared with the footer's round plate. */}
              <MarkPlate
                src={logoUrl!}
                alt={tenant.name}
                bounds={props.markBounds}
                shape="shield"
                sizeClass={BADGE_PLATE_BY_SIZE[logoSize]}
              />
            </Link>
          ) : (
            <Logo tenant={tenant} logoUrl={logoUrl} size={logoSize} plate={props.logoPlate} />
          )}
          {/* `button` too: a parent with no link renders as a toggle button. */}
          <div className={uppercase ? '[&>nav>div>:is(a,button)]:uppercase [&>nav>div>:is(a,button)]:tracking-wide' : ''}>
            <DesktopNav menuEntries={menuEntries} onLinkClick={props.onLinkClick} />
          </div>
          <div className={`flex items-center gap-4 ${uppercase ? '[&>a]:uppercase [&>a]:tracking-wide' : ''}`}>
            {/* CTA anchors the far right of the button group, icons sit
                inboard of it — the conventional order when a header carries
                both a primary CTA and account/cart affordances. */}
            <HeaderActions
              cta={data.cta}
              searchSlot={searchSlot}
              accountSlot={accountSlot}
              cartSlot={cartSlot}
              mobileNavSlot={mobileNavSlot}
              ctaLast
            />
            {/* Tap-to-text. `sms:` rather than `tel:` — the dealer's funnel is
                "call or text", and a text is the lower-friction opener.

                Placement follows the dominant convention among call-driven
                service businesses, measured 2026-08-29: Roto-Rooter, Mr. Rooter
                and Benjamin Franklin Plumbing all sit the number on the SAME
                ROW as the booking CTA and OUTBOARD of it (rightmost element),
                rather than stacking the two. Hence DOM order after
                HeaderActions.

                Renders from `lg` only. The digits do not fit below that — with
                even a 4-character label present the row overflowed by 32px at
                375px and 20px at 768px (measured 2026-08-29) — and the link
                carries no icon to fall back to, so below `lg` it is not drawn
                at all. Small screens are not left without the number:
                MobileNav renders it in full in its contact block behind the
                hamburger, which is where a phone visitor can actually read
                it. */}
            {phone && (
              <a
                href={`sms:${phone}`}
                className="hidden lg:flex items-center shrink-0 text-sm font-semibold whitespace-nowrap text-[var(--color-text-primary)] hover:opacity-80 transition-opacity"
                // The visible label (the number) is a substring of this
                // accessible name, so the link satisfies WCAG 2.5.3 Label in
                // Name — the trap the footer's directions link documents (an
                // aria-label REPLACES the name; it must therefore still
                // contain whatever is rendered). It also carries the only
                // signal that this opens a text rather than a call, now that
                // the message icon is gone.
                aria-label={`Text us at ${formatPhone(phone)}`}
                data-chrome-target="header:phone"
                onClick={
                  props.onLinkClick
                    ? () => props.onLinkClick!({ label: 'header:phone', href: `sms:${phone}` })
                    : undefined
                }
              >
                {formatPhone(phone)}
              </a>
            )}
          </div>
        </div>
      </div>
    </header>
  )
}
